import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
} from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { usePrepareFilters } from '@work4all/components';
import { CardWidget } from '@work4all/components/lib/dataDisplay/card-widget';

import { useDataProvider, useNavigate } from '@work4all/data';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { DataRequest, SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { mutationEntityToViewEntity } from '@work4all/utils';
import { useDeepMemo } from '@work4all/utils/lib/hooks/use-deep-memo';

import { stringifyTableSettings } from '../../../../components/data-tables/make-table-settings';

import {
  widgetColsByEntity,
  widgetDataByEntity,
} from './default-entity-widget-data';
import { HomeWidget } from './HomeWidgetsContextProvider';

export const EntityWidget = (
  props: HomeWidget & {
    onDeleteClick?: () => void;
    onEditClick?: () => void;
    pageSize?: number;
    onCollapsedChange?: (value: boolean) => void;
    collapsible?: boolean;
    collapsed?: boolean;
  }
) => {
  const { t } = useTranslation();

  const {
    pageSize,
    definition,
    onEditClick,
    onDeleteClick,
    title,
    collapsed,
    onCollapsedChange,
  } = props;

  const parsedPresetFields = JSON.parse(definition?.presetFields ?? '{}');
  const searchParams = useMemo(() => {
    const searchParams = new URLSearchParams();

    if (definition?.presetFields) {
      searchParams.set('presetFields', definition?.presetFields);
    }

    if (definition?.customizedTitle) {
      searchParams.set('customizedTitle', definition?.customizedTitle);
    }

    searchParams.set(
      'settings',
      stringifyTableSettings({
        filter:
          definition?.filters?.map((filter) => {
            return {
              ...filter,
              value: filter?.value?.value,
            };
          }) || [],
      })
    );

    return searchParams;
  }, [
    definition?.customizedTitle,
    definition?.filters,
    definition?.presetFields,
  ]);

  const preparedFilters = usePrepareFilters(definition?.filters);

  // We are doing this for backward compatibility for entities like `inboundInvoice`.
  const viewEntity = mutationEntityToViewEntity(definition?.entity);

  const prefilter = useDeepMemo(
    () => props.definition.prefilter,
    [props.definition.prefilter]
  );

  const requestData = useMemo<DataRequest>(() => {
    const presetFieldsFilters = Object.keys(parsedPresetFields).map((key) => {
      return { [key]: { $eq: parsedPresetFields[key] } };
    });
    const filter = [
      ...(preparedFilters.filter ?? []),
      ...(prefilter ?? []),
      ...(presetFieldsFilters.length > 0 ? presetFieldsFilters : []),
    ];

    return {
      filter,
      entity: viewEntity,
      data: { id: null, ...widgetDataByEntity[viewEntity] },
      sort: [
        {
          direction: SortDirection.DESCENDING,
          field: widgetColsByEntity[viewEntity]?.[0].field,
        },
      ],
    };
  }, [parsedPresetFields, preparedFilters.filter, viewEntity, prefilter]);

  const { data, total } = useDataProvider(requestData, false, pageSize);

  const { handleClick, ...popoverState } = usePopoverState('bottom');

  const navigate = useNavigate();

  const listHrefEntitiy = useMemo(() => {
    if (
      viewEntity === Entities.document &&
      parsedPresetFields?.infoWindowName
    ) {
      return `${Entities.document}(${parsedPresetFields?.infoWindowName})`;
    }

    return viewEntity;
  }, [parsedPresetFields?.infoWindowName, viewEntity]);

  return (
    <>
      <Popover
        {...popoverState}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        <List disablePadding>
          {onEditClick && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onEditClick();
                  popoverState.onClose();
                }}
              >
                <ListItemText primary={t('SETTINGS.SETTINGS')} />
              </ListItemButton>
            </ListItem>
          )}
          {onDeleteClick && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onDeleteClick();
                  popoverState.onClose();
                }}
              >
                <ListItemText primary={t('COMMON.REMOVE')} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Popover>
      <CardWidget
        entity={viewEntity}
        columns={widgetColsByEntity[viewEntity]}
        getItemHref={() => ''}
        listHref={`/more/entity/${listHrefEntitiy}?${searchParams.toString()}`}
        maxRows={total}
        newHref={`/more/entity/${viewEntity}/details/new`}
        items={data}
        title={title || t(`COMMON.${viewEntity.toUpperCase()}_plural`)}
        totalCount={total}
        collapsible={true}
        collapsed={collapsed}
        onItemClick={(item) => {
          navigate(`/more/entity/${viewEntity}/details/${item.id}`);
        }}
        onCollapsedChange={onCollapsedChange}
        additionalActions={
          <IconButton size="medium" onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
        }
      />
    </>
  );
};
